/**
 * Iter 78 Phase 3 — Raster Health Banner.
 *
 * Mounted INSIDE the Sheet Viewer canvas frame. Probes the
 * /api/sheets/{id}/raster_health endpoint on every sheet change. If the
 * raster is unavailable OR detected blank, surfaces a clear banner so
 * the user is NEVER confused by an empty white canvas.
 *
 * States:
 *   - hidden: ready_for_viewer === true
 *   - "Source PDF appears blank" banner: raster_available && is_blank
 *   - "Drawing unavailable" banner: !raster_available (with retry)
 */
import React, { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

export default function RasterHealthBanner({ sheetId }) {
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);

  const probe = async () => {
    if (!sheetId) { setHealth(null); return; }
    setBusy(true);
    try {
      const r = await api.get(`/sheets/${sheetId}/raster_health`);
      setHealth(r.data);
      // If the raster isn't cached yet, kick off a warm-up by requesting
      // the JPEG (the /raster route generates on-demand) and re-probe.
      if (!r.data?.raster_available) {
        // Fire-and-forget raster request to trigger generation.
        try { await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sheets/${sheetId}/raster?dpr=1`, { headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || ''}` } }); } catch {}
        // Re-probe after a beat.
        setTimeout(async () => {
          try {
            const r2 = await api.get(`/sheets/${sheetId}/raster_health`);
            setHealth(r2.data);
          } catch {}
        }, 1200);
      }
    } catch {
      setHealth({ raster_available: false, is_blank: false, ready_for_viewer: false, _err: true });
    } finally { setBusy(false); }
  };
  useEffect(() => {
    setHealth(null);
    const t = setTimeout(probe, 1500);  // small delay so the raster has a chance to warm
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetId]);

  if (!health || health.ready_for_viewer) return null;

  const blank = health.raster_available && health.is_blank;
  const missing = !health.raster_available;

  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 z-30 max-w-md bg-[var(--fop-warn)] text-black border border-[var(--fop-warn)] rounded shadow-lg px-3 py-2 flex items-center gap-2"
      data-testid="raster-health-banner"
      data-state={blank ? "blank-source" : "missing-raster"}
      style={{ minHeight: 44, top: "calc(env(safe-area-inset-top, 0px) + 1rem)" }}
    >
      <AlertTriangle size={16} className="shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold leading-tight">
          {blank ? "Source PDF appears blank" : "Drawing not yet available"}
        </div>
        <div className="text-[11px] leading-tight">
          {blank
            ? `The uploaded PDF is ${(health.source_pdf_bytes || 0) < 4000 ? "very small" : "rasterizes empty"}. Re-upload a real plan sheet.`
            : "Raster is generating. Tap to retry."}
        </div>
      </div>
      <button
        onClick={probe}
        disabled={busy}
        data-testid="raster-health-retry"
        className="shrink-0 text-[11px] font-mono uppercase tracking-wider px-2 py-1 bg-black/15 rounded flex items-center gap-1"
        title="Retry"
        style={{ minHeight: 44 }}
      >
        <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Retry
      </button>
    </div>
  );
}
